import {
  getSmartKeyTextSyntax,
  isPhraseSegment,
  isTextSegment,
  parseSmartPhrases,
  smartPhrases,
} from '@rsos/utils/SmartPhrases';
import { ChipPortal } from './SmartPhraseEditor';

export const domToRaw = (el: HTMLDivElement): string => {
  let raw = '';

  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      raw += child.textContent ?? '';
    } else if (child instanceof HTMLElement) {
      const phraseKey = child.dataset.phraseKey;

      if (phraseKey) {
        raw += getSmartKeyTextSyntax(phraseKey);
      } else if (child.tagName === 'BR') {
        raw += '\n';
      } else {
        raw += child.textContent ?? '';
      }
    }
  }

  return raw;
};

// Rebuilds the editor DOM from raw text. Chip spans are left empty —
// SmartPhraseChip components are portaled in by the component.
export const syncDOMFromValue = (el: HTMLDivElement, value: string): void => {
  el.replaceChildren();

  if (!value) {
    return;
  }

  for (const segment of parseSmartPhrases(value)) {
    if (isTextSegment(segment)) {
      el.appendChild(document.createTextNode(segment.content));
    } else if (isPhraseSegment(segment) && segment.phrase) {
      const span = document.createElement('span');

      span.contentEditable = 'false';
      span.dataset.phraseKey = segment.phrase.key;
      el.appendChild(span);
    } else {
      el.appendChild(
        document.createTextNode(getSmartKeyTextSyntax(segment.key)),
      );
    }
  }
};

export const buildChipPortals = (el: HTMLDivElement): ChipPortal[] => {
  const portals: ChipPortal[] = [];

  for (const child of Array.from(el.childNodes)) {
    if (child instanceof HTMLElement && child.dataset.phraseKey) {
      const phrase = smartPhrases.find(p => p.key === child.dataset.phraseKey);

      if (phrase) {
        portals.push({ el: child, phrase });
      }
    }
  }

  return portals;
};

export const getCursorTextOffset = (el: HTMLDivElement): number => {
  const sel = window.getSelection();

  if (!sel || sel.rangeCount === 0) {
    return 0;
  }

  const range = sel.getRangeAt(0);

  if (!el.contains(range.startContainer) && range.startContainer !== el) {
    return 0;
  }

  let offset = 0;

  for (let i = 0; i < el.childNodes.length; i++) {
    const child = el.childNodes[i];

    if (range.startContainer === el && range.startOffset <= i) {
      break;
    }

    if (child.nodeType === Node.TEXT_NODE) {
      if (range.startContainer === child) {
        offset += range.startOffset;
        break;
      }

      offset += child.textContent?.length ?? 0;
    } else if (child instanceof HTMLElement && child.dataset.phraseKey) {
      offset += getSmartKeyTextSyntax(child.dataset.phraseKey).length;
    }
  }

  return offset;
};

const applyRange = (sel: Selection, range: Range): void => {
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const setCursorTextOffset = (
  el: HTMLDivElement,
  targetOffset: number,
): void => {
  const sel = window.getSelection();

  if (!sel) {
    return;
  }

  let remaining = targetOffset;

  for (let i = 0; i < el.childNodes.length; i++) {
    const child = el.childNodes[i];

    if (child.nodeType === Node.TEXT_NODE) {
      const len = child.textContent?.length ?? 0;

      if (remaining <= len) {
        const range = document.createRange();

        range.setStart(child, remaining);
        applyRange(sel, range);

        return;
      }

      remaining -= len;
    } else if (child instanceof HTMLElement && child.dataset.phraseKey) {
      const tokenLen = getSmartKeyTextSyntax(child.dataset.phraseKey).length;

      if (remaining <= tokenLen) {
        const range = document.createRange();

        range.setStartAfter(child);
        applyRange(sel, range);

        return;
      }

      remaining -= tokenLen;
    }
  }

  // Cursor at end of content
  const range = document.createRange();

  if (el.lastChild) {
    if (el.lastChild.nodeType === Node.TEXT_NODE) {
      range.setStart(el.lastChild, el.lastChild.textContent?.length ?? 0);
    } else {
      range.setStartAfter(el.lastChild);
    }
  } else {
    range.setStart(el, 0);
  }

  applyRange(sel, range);
};
